import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { searchCommunities } from '../../actions/communitiesActions'
import { isValidEmail } from '../../utility/helperFunctions'
import Select from '../common/form/Select'
import Input from '../common/form/Input'
import Modal from '../common/Modal'
import CancelButton from '../common/buttons/CancelButton'
import SubmitButton from '../common/buttons/SubmitButton'

import classes from './InviteCoachModal.module.scss'

const customErrorMessages = {
  firstName: 'First name must be present',
  lastName: 'Last name must be present',
  email: 'Your email address is not valid',
  communityId: 'Community must be present',
}

const InviteCoachModal = ({ isOpen, onClose, onSubmit }) => {
  const dispatch = useDispatch()
  const communities = useSelector(state => state.communities.communities || [])

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [communityId, setCommunityId] = useState('')

  const [communityOptions, setCommunityOptions] = useState([])
  const [communitySearchTerm, setCommunitySearchTerm] = useState('')
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  const formValid = () => {
    const errors = {}
    const fieldValues = { firstName, lastName, email, communityId };
    ['firstName', 'lastName', 'email', 'communityId'].forEach((field) => {
      if (String(fieldValues[field] || '').trim() === '') { errors[field] = customErrorMessages[field] }
    })
    if (email && !isValidEmail(email)) { errors.email = customErrorMessages.email }
    setErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formValid()) { return }

    const inviteData = {
      first_name: firstName,
      last_name: lastName,
      email,
      community_id: communityId,
      user_setting_attributes: { coach_permission: 1 },
    }

    setIsSubmitting(true)
    onSubmit(inviteData).finally(() => setIsSubmitting(false))
  }

  const handleChange = (field, value) => {
    const setters = { firstName: setFirstName, lastName: setLastName, email: setEmail }
    setters[field](value)
    setErrors(prev => ({ ...prev, [field]: null }))
  }

  const handleSelectChange = (selectedOption) => {
    const value = selectedOption ? selectedOption.value : ''
    setCommunityId(value)
    setErrors(prev => ({ ...prev, communityId: null }))
  }

  const handleCommunitySearch = (inputValue) => {
    setCommunitySearchTerm(inputValue)
    if (inputValue && inputValue.length >= 3) {
      setIsLoadingCommunities(true)
      dispatch(searchCommunities(inputValue))
        .then(response => setCommunityOptions(response.data.communities.map(c => ({ value: c.id, label: c.name }))))
        .finally(() => setIsLoadingCommunities(false))
    } else {
      setCommunityOptions([])
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className={classes.inviteCoachModalContent}
    >
      <div className={classes.inviteCoachModal}>
        <div className='d-flex flex-column mb-5'>
          <h2 className={classes.modalTitle}>Invite Coach</h2>
          <p className={classes.modalSubtitle}>
            Invite a new coach to a community, they will create their own account.
          </p>
        </div>

        <form onSubmit={handleSubmit} className={classes.modalForm}>
          <div className='row pb-4'>
            <div className='col-12'>
              <div className='row'>
                <div className='col-12 col-md-6 mb-3'>
                  <label htmlFor='firstName'>
                    First Name <span className='required-text'>*</span>
                  </label>
                  <Input
                    id='firstName'
                    type='text'
                    value={firstName}
                    onChange={(e) => handleChange('firstName', e.target.value)}
                    error={errors.firstName}
                  />
                </div>
                <div className='col-12 col-md-6 mb-3'>
                  <label htmlFor='lastName'>
                    Last Name <span className='required-text'>*</span>
                  </label>
                  <Input
                    id='lastName'
                    type='text'
                    value={lastName}
                    onChange={(e) => handleChange('lastName', e.target.value)}
                    error={errors.lastName}
                  />
                </div>
              </div>

            </div>

            <div className='col-12 mb-3'>
              <label htmlFor='email'>
                Email <span className='required-text'>*</span>
              </label>
              <Input
                id='email'
                type='text'
                value={email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={errors.email}
              />
            </div>

            <div className='col-12 mb-3'>
              <label htmlFor='community'>
                Community <span className='required-text'>*</span>
              </label>
              <Select
                inputId='community'
                value={communityId ? communities.find(c => c.value === communityId) : null}
                onChange={(selectedOption) => handleSelectChange(selectedOption)}
                onInputChange={handleCommunitySearch}
                options={communityOptions}
                placeholder='Search for Community (min 3 characters)'
                isLoading={isLoadingCommunities}
                filterOption={null}
                noOptionsMessage={() => communitySearchTerm.length < 3 ? 'Type at least 3 characters to search' : 'No communities found'}
                error={errors.communityId}
              />
            </div>
          </div>

          <div className='row pt-4'>
            <div className='col-6'>
              <CancelButton onClick={onClose} buttonMessage='Cancel' />
            </div>
            <div className='col-6'>
              <SubmitButton type='submit' buttonMessage='Send Invite' loading={isSubmitting} noTopMargin />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  )
}

InviteCoachModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
}

export default InviteCoachModal
